// Data source configuration keys
import { isNullish } from "@theorgchartdev/storybook";
import { getImagePath } from "@coreapp/utils/getImagePath";
import {
  getChartSettingsTestPrefix,
  ChartSettingsSectionRoutes
} from "@/providers/NavigationProvider";
import {
  kDayforceTables,
  kUKGTables,
  kUKGReadyTables,
  kISolvedTables,
  kPaychexTables
} from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { convertStdDateString } from "@coreapp/utils/convertStdDateString";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList } from "@coreapp/constants";
import { gENAppsString } from "@coreapp/Localization/QENStrings";

export const kConnectorDataKeys = {
  activedir: ["datadir", "datafile"],
  AzureAD: ["subdomain", "clientID", "clientSecret"],
  Bamboo: ["subdomain", "apiKey"],
  BreatheHR: ["apiKey"],
  Cornerstone: ["subdomain", "clientID", "clientSecret"],
  Dayforce: ["dforg", "dfuser", "dfpass"],
  CSV: ["datadir", "datafile"],
  Igloo: ["appID", "appPass", "username", "password", "community"],
  isolved: ["domain", "clientCode"],
  Jemini: ["subdomain", "apiKey"],
  Lever: ["apiKey"],
  // Three auth modes, so no single list describes Greenhouse. Left as-is because nothing reads
  // it here: AreCredsFilled only feeds AuthValidationComposite, which Greenhouse never renders.
  Greenhouse: ["clientID", "clientSecret"],
  XLS: ["datadir", "datafile"],
  Namely: ["subdomain", "apiKey"],
  ocAPI: [],
  Oracle: ["subdomain", "username", "password", "expand"],
  Paychex: ["clientID", "clientSecret", "companyID"],
  Paycom: ["username", "password"],
  Paycor: [
    "domain",
    "clientID",
    "clientSecret",
    "subscriptionKey",
    "legalEntityId",
    "refreshToken"
  ],
  Rippling: ["apiKey"],
  paylocity: ["companyID"],
  paylocity_nextgen: ["companyID", "clientID", "clientSecret"],
  PaySpace: ["clientID", "clientSecret", "psreqtype", "psterm"],
  PeopleHR: ["apiKey"],
  SAP: ["orgfile", "personfile"], // USPS's specific SAP handling
  "SAP RFC": [
    "sapviewid",
    "sapobjectid",
    "sapobjecttype",
    "saphost",
    "sapclient",
    "sapsystem",
    "sapuser",
    "sappassword"
  ],
  SFTP: ["host", "port", "directory", "filename", "username", "password"],
  Sage: ["username", "password", "token"],
  Jobvite: ["apiKey", "apiSecret"],
  ICIMS: ["customerID", "user", "password"],
  "Sage 300 ZA": ["site", "apiKey"],
  Salesforce: ["username", "password", "token", "query"],
  Signify: ["token"],
  SuccessFactors: ["service", "userid", "pstr", "sfsforg", "resource", "querystring"],
  OrangeHRM: ["subdomain", "clientID", "clientSecret"],
  "Thrive TRM": ["resource", "apiKey"],
  TriNet: ["companyID", "clientID", "clientSecret"],
  UKG: ["subdomain", "username", "password", "clientkey"],
  "UKG Ready": ["subdomain", "company", "apikey", "username", "password"],
  Ultipro: ["interface", "subdomain", "userkey", "clientkey", "username", "password"],
  Workday: ["workdayuser", "workdaypstr", "workdayhc"],
  WorkdayAPI: ["domain", "accountName", "clientID", "clientSecret", "refreshToken"],
  Manual: ["datadir", "datafile"],
  OPP: [
    "apiRoot",
    "dataPath",
    "tokenUser",
    "tokenPassword",
    "dataUser",
    "dataPassword",
    "auth0url",
    "auth0clientId",
    "auth0clientSecret",
    "audience",
    "othLoginUrl",
    "serverId"
  ]
};

export const kConnectorLegacyDataKeys = {
  AzureAD: { clientID: ["id"], clientSecret: ["secret"] }
};

export const kAzureCloudEnvironmentOptions = [
  { value: "commercial", label: "Standard (.com)" },
  { value: "gcc_high", label: "US government (.us)" }
];

export const BuildAzureCloudEnvironmentOptions = clickHandler =>
  kAzureCloudEnvironmentOptions.map(option => ({
    value: option.value,
    label: appstr(option.label),
    clickHandler
  }));

export const enumGreenhouseAuthModes = {
  v1: "v1",
  clientCredentials: "clientCredentials",
  partnerOAuth: "partnerOAuth"
};

// Mirrors the connector's credential precedence (Greenhouse.py getGreenhouseData): refreshToken,
// then clientID+clientSecret, then apiKey. Disagreeing here shows one credential in the panel
// while the sync authenticates with another. useV1 is absent on every pre-v3 source, so it has
// to default true; a refreshToken then outranks a migrated customer's stale clientID.
export const GetGreenhouseAuthMode = config => {
  if (config?.useV1 !== false) return enumGreenhouseAuthModes.v1;
  if (config?.refreshToken) return enumGreenhouseAuthModes.partnerOAuth;
  return config?.clientID
    ? enumGreenhouseAuthModes.clientCredentials
    : enumGreenhouseAuthModes.partnerOAuth;
};

// v1 is retired 2026-08-31, so a source that never held v1 credentials must not opt into it.
// Keyed on apiKey rather than the resolved mode so a customer who unchecks the box can check it
// again. A refreshToken ends that: it outranks apiKey in the connector, so re-checking would
// show the API Key field while the sync kept authenticating over OAuth.
export const ShouldShowGreenhouseV1Toggle = config =>
  !config?.refreshToken &&
  (GetGreenhouseAuthMode(config) === enumGreenhouseAuthModes.v1 || Boolean(config?.apiKey));

// New sources start on v3: v1 is retired 2026-08-31 and partners may not collect the customer's
// own credentials. Guarded on the slot not already being Greenhouse, since existing sources carry
// no useV1 key and must keep falling back to v1 rather than being migrated by a re-pick.
export const ApplyNewSourceAuthDefaults = (updatedObj, existingConfig, importType) => {
  if (updatedObj?.source === "Greenhouse" && existingConfig?.source !== "Greenhouse") {
    updatedObj.useV1 = false;
  }
  if (updatedObj?.source === "ocAPI" && importType !== "import" && !updatedObj.sourceId) {
    updatedObj.sourceId =
      existingConfig?.source === "ocAPI" && existingConfig?.sourceId
        ? existingConfig.sourceId
        : `${importType}_${crypto.randomUUID()}`;
  }
  return updatedObj;
};

export const kConnectorOptionalKeys = {
  Paychex: ["domain"],
  Rippling: ["domain"],
  PaySpace: ["pspljobs"],
  "Sage 300 ZA": ["port"],
  Signify: ["subdomain", "photourl", "schemaid"],
  Oracle: ["finder", "query", "limit"],
  ICIMS: ["filter", "fields"],
  AzureAD: ["additionalFields", "filter"],
  Ultipro: ["reportname", "reportpath"],
  Dayforce: ["dfIDPrefix"]
};

export const kConnectorPhotoOptionalKeys = {
  Oracle: ["finder", "query"]
};

export const kKeyToLabelAndDefaultText = {
  // Generic(-ish) Labels
  // ultipro
  apiKey: ["genericapikey1", "genericapikey2"],
  apikey: ["genericapikey1", "genericapikey2"],
  domain: ["genericdomain1", "genericdomain2"],
  subdomain: ["genericsubdomain1", "genericsubdomain2"],
  id: ["genericclientid1", "genericclientid2"],
  secret: ["genericclientsecret1", "genericclientsecret2"],
  apiSecret: ["genericclientsecret1", "genericclientsecret2"],
  clientID: ["genericclientid1", "genericclientid2"],
  clientSecret: ["genericclientsecret1", "genericclientsecret2"],
  appID: ["genericappid1", "genericappid2"],
  appPass: ["genericapppass1", "genericapppass2"],
  username: ["genericusername1", "genericusername2"],
  userkey: ["upuseraccesskey1", "upuseraccesskey2"],
  clientkey: ["upclientaccesskey1", "upclientaccesskey2"],
  companyID: ["genericcompanyid1", "genericcompanyid2"],
  company: ["genericcompany1", "genericcompany2"],
  interface: ["ultiproInterface1", "ultiproInterface2"],
  password: ["genericpassword1", "genericpassword2"],
  community: ["genericcommunity1", "genericcommunity2"],
  service: ["sfsfservice", "sfsfservice2"],
  userid: ["sfsfuser", "sfsfuser2"],
  port: ["genericport1", "genericport2"],
  site: ["genericendpoint1", "genericendpoint2"],
  filter: ["genericfilter1", "genericfilter2"],
  fields: ["genericadditionalfields1", "genericadditionalfields2"],
  clientCode: ["genericclientcode1", "genericclientcode2"],
  // Connector-specific labels
  pspljobs: ["payspaceoptions", "payspaceoptions"],
  pstr: ["sfsfpassword", "sfsfpassword2"],
  psterm: ["payspaceterm", "payspaceterm2"],
  sfsforg: ["sfsforg", "sfsforg2"],
  resource: ["sfsfresource", "sfsfresource"],
  workdayuser: ["workdayapiuser", "workdayapiuser2"],
  workdaypstr: ["workdaypassword", "workdaypassword2"],
  workdayhc: ["genericendpoint1", "workdayendpoint2"],
  dforg: ["genericcompanyid1", "genericcompanyid2"],
  dfuser: ["genericusername1", "genericusername2"],
  dfpass: ["genericpassword1", "genericpassword2"],
  dfActiveXRefCodes: ["dfstatus1", "dfstatus2"],
  dfendpoint: ["genericendpoint1", "genericendpoint2"],
  psreqtype: ["payspacereqtype", "payspacereqtype2"],
  token: ["sftoken", "salesforcecerr3"],
  photourl: ["Photo URL", "Leave blank to match subdomain"],
  schemaid: ["genericschemaid1", "genericschemaid2"],

  //oracle
  expand: ["Child resources", "Child resources helper"],
  finder: ["Finder", "Finder helper"],
  query: ["Parameters", "Parameters helper"],
  limit: ["limitlabel", "limithelper"],

  //icims
  customerID: ["icimscusid", "icimscusid2"],
  user: ["icimsuser", "icimsuser2"],

  //succesfactors
  querystring: ["sfsfquerystring", "sfsfquerystring2"],

  //azure
  tenantID: ["genericsubdomain1", "genericsubdomain2"],
  additionalFields: ["genericadditionalfields1", "genericadditionalfields2"],
  reportname: ["ultiproReportName1", "ultiproReportName2"],
  reportpath: ["ultiproReportPath1", "ultiproReportPath2"],

  //saprfc
  sapviewid: ["sapviewid", "sapviewid2"],
  sapobjectid: ["sapobjectid", "sapobjectid2"],
  sapobjecttype: ["sapobjecttype", "sapobjecttype2"],
  saphost: ["saphost", "saphost2"],
  sapclient: ["sapclient", "sapclient2"],
  sapsystem: ["sapsystem", "sapsystem2"],
  sapuser: ["sapuser", "sapuser2"],
  sappassword: ["sappassword", "sappassword2"],
  accountName: ["genericaccountname1", "genericaccountname2"],
  refreshToken: ["genericrefreshtoken1", "genericrefreshtoken2"],
  subscriptionKey: ["paycorsubkey1", "paycorsubkey2"],
  legalEntityId: ["paycorlegentityid1", "paycorlegentityid2"],
  othLoginUrl: ["othLoginUrl", "othLoginUrl"],
  resourceId: ["resourceId", "resourceId2"]
};

export const kConnectorTEParams = {
  query: { type: "textarea" },
  querystring: { type: "textarea" },
  workdayhc: { type: "textarea" }
};

// Must be lower-cased.
export const kPasswordMaskingKeys = [
  "apikey",
  "apisecret",
  "apppass",
  "auth0clientsecret",
  "clientkey",
  "clientsecret",
  "subscriptionkey",
  "refreshtoken",
  "datapassword",
  "database",
  "dfpass",
  "token",
  "tokenpassword",
  "sappassword",
  "userkey",
  "password",
  "pspassword",
  "pstr",
  "workdaypstr"
];

export const kConnectorAllowAuthValidation = [
  "AzureAD",
  "Bamboo",
  "Dayforce",
  "Paycom",
  "paylocity",
  "PaySpace",
  "Sage 300 ZA",
  "paylocity_nextgen",
  "SuccessFactors",
  "Ultipro",
  "Workday",
  "WorkdayAPI"
];

export const kATSConnectorsWithStaticMapping = ["Greenhouse", "ICIMS", "Jobvite", "Lever"];

export const kAppendDisplayFieldMappingConnectors = ["Ultipro"];

export const kAppendDisplayFieldsByDataType = {
  employees: ["Name", "Title"],
  positions: ["Position Title"]
};

export const GetAppendDisplayFields = dataType =>
  kAppendDisplayFieldsByDataType[dataType] ?? kAppendDisplayFieldsByDataType.employees;

export const GetAppendDataType = config => config?.dataType ?? "employees";

export const kDatasetTypeSelectionConnectors = ["CSV", "XLS", "SAP", "SAP XML", "Ultipro", "ocAPI"];

export const ShowDatasetTypeOptions = (chartConfig, importType, source) =>
  IsPositionChart(chartConfig) &&
  ["append", "merge"].includes(importType) &&
  kDatasetTypeSelectionConnectors.includes(source);

export const GetLastValidationTime = function (chart, type = "import") {
  // TO-DO: add handling for other submodes as appropriate...
  var syncObj = (chart["sync"] = chart["sync"] || {});
  var diagnosticsObj = (syncObj["diagnostics"] = syncObj["diagnostics"] || {});
  var authenticationObj;
  switch (type) {
    case "import":
      var mainObj = (diagnosticsObj["main"] = diagnosticsObj["main"] || {});
      authenticationObj = mainObj["authentication"] = mainObj["authentication"] || {};
      break;
    case "photo":
      var photoObj = (diagnosticsObj["photo"] = diagnosticsObj["photo"] || {});
      authenticationObj = photoObj["authentication"] = photoObj["authentication"] || {};
      break;
    default:
      authenticationObj = {};
  }
  return authenticationObj["timeStamp"];
};

// TODO uhhhhh make this better
export const SetLastValidationTime = function (chart, type = "import", timeStamp) {
  var syncObj = (chart["sync"] = chart["sync"] || {});
  var diagnosticsObj = (syncObj["diagnostics"] = syncObj["diagnostics"] || {});
  switch (type) {
    case "import":
      var mainObj = (diagnosticsObj["main"] = diagnosticsObj["main"] || {});
      var authenticationObj = (mainObj["authentication"] = mainObj["authentication"] || {});
      break;
    case "photo":
      var photoObj = (diagnosticsObj["photo"] = diagnosticsObj["photo"] || {});
      var authenticationObj = (photoObj["authentication"] = photoObj["authentication"] || {});
      break;
    default:
      var authenticationObj = {};
  }
  authenticationObj["timeStamp"] = timeStamp;
};

export const GetLastChartValidationTimeLabel = function (lastAuthenticated) {
  var lastAuthenticatedFormatted = lastAuthenticated
    ? convertStdDateString(lastAuthenticated)
    : null;
  var authHover = lastAuthenticated
    ? appstr("lastvalidation") + ": " + lastAuthenticatedFormatted
    : appstr("notvalidated");
  var authLabel = appstr("Authorization") + ": ";
  authLabel += lastAuthenticated ? "Success" : "Failure";
  authHover = authHover.substr(0, 37);

  return {
    hover: authHover,
    label: authLabel
  };
};

export const GetAdditionalTableFields = source => {
  let fieldList = [];
  switch (source) {
    default:
      break;
    case "Dayforce":
      fieldList = kDayforceTables;
      break;
    case "UKG":
      fieldList = kUKGTables;
      break;
    case "UKG Ready":
      fieldList = kUKGReadyTables;
      break;
    case "isolved":
      fieldList = kISolvedTables;
      break;
    case "Paychex":
      fieldList = kPaychexTables;
      break;
  }
  return fieldList;
};

// Only called when changing from position based chart <-> employee based chart
/*
const ResetDataSourceOptions = (chartConfig, resetFieldMap, resetDiagKeys) => {
  let updatedChartConfig = JSON.parse(JSON.stringify(chartConfig));
  if (!chartConfig) return;
  let changedKeys = ["headers", "topBoxID", "topBoxName"];
  // reset headers
  updatedChartConfig["headers"] = null;
  // reset topbox
  updatedChartConfig["topBoxID"] = null;
  updatedChartConfig["topBoxName"] = null;
  // reset mapping for non csv / excel file types
  if (resetFieldMap) {
    updatedChartConfig["fields"] = null;
    changedKeys.push("fields");
  }
  if (updatedChartConfig["sync"] && updatedChartConfig["sync"]["diagnostics"]) {
    if (Array.isArray(resetDiagKeys)) {
      for (var i in resetDiagKeys) {
        var diagKey = resetDiagKeys[i];
        if (updatedChartConfig["sync"]["diagnostics"][diagKey])
          delete updatedChartConfig["sync"]["diagnostics"][diagKey];
      }
      changedKeys.push("sync");
    } else if (resetDiagKeys === true) {
      changedKeys.push("sync");
      delete updatedChartConfig["sync"];
    }
  }
  return { updatedChartConfig, changedKeys };
};
 */

/** returns a new list */
export function toggleValueInList(list, valueToToggle) {
  let valuePresent = false;
  const filteredList = list.filter(v => {
    if (v === valueToToggle) {
      valuePresent = true;
      return false;
    }
    return true;
  });
  if (!valuePresent) {
    return filteredList.concat([valueToToggle]);
  } else {
    return filteredList;
  }
}

export const DATA_SOURCES_TEST_PREFIX = getChartSettingsTestPrefix(
  ChartSettingsSectionRoutes.DataSource
);

export const IsDataSourceSet = dataSource => {
  return dataSource !== "" && !isNullish(dataSource);
};

export const connectorFlatFileList = [
  "Manual",
  "CSV",
  "XLS",
  "SAP XML",
  "SAP",
  "UnicornHRO",
  "OCN API",
  "SmartChart API"
];

export const seccondaryAutoLoadConnectors = [
  "ADP Canada",
  "ADP Enterprise",
  "ADP Europe",
  "Vantage",
  "ADP",
  "Cornerstone",
  "Paycom",
  "TriNet",
  "Thrive TRM",
  "UKG Ready"
];

export const getDataSourceMessage = chartConfig => {
  let usedSources = chartConfig.append.length + chartConfig.merge.length;
  let totalSources = getFeatureAccess(enumFeaturesList.dataConnectorLimit);
  let remainingSources = totalSources - usedSources;
  if (remainingSources < 0) remainingSources = 0;

  let dataSourcesMsg = appstr("Additional data sources remaining") + ": " + remainingSources;

  return dataSourcesMsg;
};

export const upgradePillLabel = appstr("Upgrade to add");

export const getAppendUpgradeText = () => {
  return {
    message: appstr("append message"),
    template: appstr("append template"),
    formatConfig: {
      bold1: { type: "bold" },
      bold2: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("additional HR systems"),
      bold2: appstr("vacancies or contractors")
    }
  };
};

export const getMergeUpgradeText = () => {
  return {
    message: appstr("merge message"),
    template: appstr("merge template"),
    formatConfig: {
      bold1: { type: "bold" },
      bold2: { type: "bold" }
    },
    replaceDict: {
      bold1: appstr("additional HR systems"),
      bold2: appstr("secondary managers")
    }
  };
};

export const GetDataSourceLogo = source => {
  let logo = null;
  //TODO: This function should be replaced by supplementing the connector icon prop in qwhitelabel.
  switch (source) {
    case "ADP":
    case "ADP Enterprise":
    case "Vantage":
    case "ADP Canada":
    case "ADP Europe":
    case "ADP Detail":
      logo = "/static/images/ADP.png";
      break;
    case "ADP Lifion":
    case "WFN Next Generation":
      logo = "/static/images/ADPNG.png";
      break;
    case "Append Plan":
      logo = "/static/images/Append Plan.svg";
      break;
    case "Append Table":
      logo = "/static/images/Append Table.svg";
      break;
    case "ADP Recruit Management":
    case "ADP WFN CA Recruitment Module":
    case "ADP Recruitment":
      logo = "/static/images/ADP Recruitment.svg";
      break;
    case "Bamboo":
      logo = "/static/images/BambooHR.png";
      break;
    case "BreatheHR":
      logo = "/static/images/BreatheHR.png";
      break;
    case "CSV":
      logo = "/static/images/CSV.svg";
      break;
    case "Google Directory":
      logo = "/static/images/Google.svg";
      break;
    case "Jobvite":
      logo = "/static/images/Jobvite.png";
      break;
    case "ODBC":
      logo = "/static/images/ODBC.svg";
      break;
    case "paylocity":
    case "paylocity_nextgen":
      logo = "/static/images/Paylocity.svg";
      break;
    case "PeopleHR":
      logo = "/static/images/PeopleHR.png";
      break;
    case "Sage 300 ZA":
      logo = "/static/images/Sage.svg";
      break;
    case "Signify":
      logo = "/static/images/Signify.png";
      break;
    case "SubscribeHR":
      logo = "/static/images/Subscribe HR.png";
      break;
    case "UKG":
    case "UKG Pro":
    case "Ultipro":
    case "UKG Ready":
      logo = "/static/images/UKG.png";
      break;
    case "SAP":
    case "SAP XML":
    case "SAP RFC":
      logo = "/static/images/SAP.png";
      break;
    case "XLS":
      logo = "/static/images/Excel.svg";
      break;
    case "ICIMS":
      logo = "/static/images/iCIMS.svg";
      break;
    case "isolved":
      logo = "/static/images/isolved.png";
      break;
    case "WorkdayAPI":
      logo = "/static/images/Workday.svg";
      break;
    default:
      logo = "/static/images/" + source + ".svg";
  }
  return getImagePath(logo);
};
